import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IStructuredLogger} from "../../../common/interfaces/structured-logging-service-interface";
import {IGeoRunResult, GeoValidationResult} from "./strategies/geo-location.strategy.interface";
import {GENERIC_GEO_ERROR_CODE, GeoComplyFailureReason} from "./strategies/geo-comply-error-codes";
import {IGeoRestrictionsUpdateEventParams} from "../sdk/sdk-events";
import {GeoRunnerName} from "./geo-runner/geo-location-runner.interface";
import {Check} from "@sparkware/app-utils";

export enum GeoAnalyticsContext {
    Login = "Login",
    OpenGame = "OpenGame",
    OpenGameUserBlocked = "OpenGameUserBlocked",
    OpenGameTokenFailed = "OpenGameTokenFailed",
    GeoKick = "GeoKick",
    PeriodicUpdate = "PeriodicUpdate",
    IpChangeDetection = "IpChangeDetection"
}

export enum GeoAnalyticsUserAction {
    ShowDownloadPlugin = "ShowDownloadPlugin",
    DownloadPlugin = "DownloadPlugin",
    ReDownloadPlugin = "ReDownloadPlugin",
    CancelDownloadPlugin = "CancelDownloadPlugin",
    OK = "OK",
    XClose = "XClose",
    Logout = "Logout",
    Retry = "Retry",
    ContactUs = "ContactUs",
    ShowGameRestrictedPopup = "ShowGameRestrictedPopup",
    ShowKickDialog = "ShowKickDialog",
    ShowGenericKickDialog = "ShowGenericKickDialog",
    OpenProgress = "OpenProgress",
    CloseProgress = "CloseProgress",
    HideProgress = "HideProgress",
    DisplayProgress = "DisplayProgress"
}

export enum GeoAnalyticsEvent {
    GeoResult = "GeoResult",
    UserAction = "UserAction",
    TroubleshooterAutoRetry = "TroubleshooterAutoRetry",
    TroubleshooterRetry = "TroubleshooterRetry",
    OrbitAutoRetry = "OrbitAutoRetry",
    UpdateRestrictionsReceived = "UpdateRestrictionsReceived",
    UpdateRestrictionsTimedOut = "UpdateRestrictionsTimedOut",
    HybridGeoTimeout = "HybridGeoTimeout",
    GeoComplyTimeout = "GeoComplyTimeout",
    CGPValidationSuccess = "CGPValidationSuccess",
    CGPValidationFailure = "CGPValidationFailure",
    GeoComplyInvalidResponse = "GeoComplyInvalidResponse",
    InitRetrySuccess = "InitRetrySuccess",
    InitRetryFailure = "InitRetryFailure",
    ParseHybridGeoResponseFailure = "ParseHybridGeoResponseFailure",
    StartLoginGeo = "StartLoginGeo",
    PeriodicGeo = "PeriodicGeo",
    StartOpenGameGeo = "StartOpenGameGeo",
    StartRetryOnOpenGame = "StartRetryOnOpenGame",
    GeoKick = "GeoKick",
    IpChangeDetected = "IpChangeDetected",
    IpChangeDetectionFailure = "IpChangeDetectionFailure",
    MaxRetryReached = "MaxRetryReached",
    GeoDuration = "GeoDuration"
}



interface IGeoAnalyticsKickParams {
    kickReasonId: string;
    retry: boolean;
    messages?: string[];
}

export interface IGeoLocationDuration {
    geoComply: number;
    attempts: number;
    platformValidation: number;
    total: number;

}

interface IGeoFlowAnalyticsData {
    context: GeoAnalyticsContext;
    runner: GeoRunnerName;
    geoEventName: GeoAnalyticsEvent;
    transactionId: string;
    userAction: GeoAnalyticsUserAction;
    status: GeoValidationResult;
    failReason: number;
    errorCode: number;
    errorMessage: string;
    retryCount: number;
    kickParams: IGeoAnalyticsKickParams;
    gameType: number;
    prevClientIp: string;
    newClientIp: string;
    durations: Partial<IGeoLocationDuration>;
}

export interface IGeoLocationUserActionAnalytics {
    reportUserAction(actionName: GeoAnalyticsUserAction): void;
}

export class GeoLocationAnalytics implements IGeoLocationUserActionAnalytics {
    constructor(private readonly _context: GeoAnalyticsContext, private readonly _geoRunnerName: GeoRunnerName, private readonly services: IServiceFactoryExtended) {
        this._correlationId = services.tools.getGuid();
    }

    private readonly _correlationId: string;

    private _transactionId: string = '';
    setTransactionId(value: string): void {
        this._transactionId = value;
    }


    private _createLogger(eventData: Partial<IGeoFlowAnalyticsData>): IStructuredLogger {

        eventData = {
            context: this._context,
            runner: this._geoRunnerName,
            transactionId: this._transactionId,
            failReason: 0,
            errorCode: 0,
            errorMessage: '',
            ...eventData
        }

        return this.services.analytics.getLogger({
            geoLocation: {
                ...eventData
            }
        }).setCorrelationId(this._correlationId)
          .setEventType('geoLocation', 'Log');
    }

    reportIpChangeDetection(previousIp: string, currentIp: string): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.IpChangeDetected,
            prevClientIp: previousIp,
            newClientIp: currentIp
        }).info();
    }

    reportIpDetectionFailure(errorMessage: string): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.IpChangeDetectionFailure,
            errorCode: GENERIC_GEO_ERROR_CODE,
            errorMessage: errorMessage
        }).info();
    }

    reportGeoResult(geoResult: IGeoRunResult): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.GeoResult,
            status: geoResult.validationResult,
            failReason: geoResult.geoComplyFailure?.additionalFailureReason ?? 0,
            errorCode: geoResult.geoComplyFailure?.geoComplyError?.errorCode ?? 0,
            errorMessage: geoResult.geoComplyFailure?.geoComplyError?.errorMessage,
        }).info();
    }

    reportGeoFailure(err: any): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.GeoResult,
            status: GeoValidationResult.Fail,
            failReason: GeoComplyFailureReason.TechnicalProblem,
            errorCode: GENERIC_GEO_ERROR_CODE,
            errorMessage: err?.toString(),
        }).info();
    }

    reportGeoDuration(durations: IGeoLocationDuration): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.GeoDuration,
            durations: durations
        }).info();
    }

    reportUserAction(actionName: GeoAnalyticsUserAction): void {
        this._createLogger({
            geoEventName: GeoAnalyticsEvent.UserAction,
            userAction: actionName
        }).info();
    }

    reportEvent(params: {
            geoEventName: GeoAnalyticsEvent;
            tryCount?: number;
            errorCode?: number;
            errorMessage?: string;
            failureReason?: GeoComplyFailureReason,
            gameType?: number,
            kickParams?: IGeoRestrictionsUpdateEventParams,
            durations?: Partial<IGeoLocationDuration>
    }): void {
        const eventData: Partial<IGeoFlowAnalyticsData> = {
            geoEventName: params.geoEventName
        }

        if(!Check.isNullOrUndefined(params.tryCount)) {
            eventData.retryCount = params.tryCount;
        }

        if(!Check.isNullOrUndefined(params.errorCode)) {
            eventData.errorCode = params.errorCode;
        }

        if(!Check.isNullOrUndefined(params.errorMessage)) {
            eventData.errorMessage = params.errorMessage;
        }

        if(!Check.isNullOrUndefined(params.failureReason)) {
            eventData.failReason = params.failureReason;
        }

        if(!Check.isNullOrUndefined(params.gameType)) {
            eventData.gameType = params.gameType;
        }

        if(!Check.isNullOrUndefined(params.kickParams)) {
            eventData.kickParams = {
                kickReasonId: params.kickParams.KickReasonId,
                retry: params.kickParams.TroubleshooterRetry,
                messages: params.kickParams.DetailedMessages
            };
        }

        if(!Check.isNullOrUndefined(params.durations)) {
            eventData.durations = params.durations
        }

        this._createLogger(eventData).info();
    }
}
